'use client';

/**
 * Unified right-click / long-press menu for the workspace canvas.
 *
 * Renders the same labeled-row layout that `FileContextMenu` uses
 * (glass panel, viewport-flip, portal escape) but reads its items
 * from the workspace command registry so menus, the floating toolbar,
 * and keyboard shortcuts all stay in lockstep.
 *
 * Pattern matches the user's "Confluence / macOS labeled list with
 * right-aligned shortcut hints" choice — no icon-button quick row at
 * the top. Icons sit at the LEFT of each row to anchor the eye but
 * the action labels still own the dominant visual weight.
 *
 * Adds vs the old menu:
 *   - Roving focus (Arrow Up / Down, Home / End, Enter / Space).
 *   - Submenu support (recursive `SubmenuItem`s). Used for the
 *     Align / Distribute pair so the multi-select menu doesn't sprout
 *     a 6-row flat list.
 *   - "Extra" items injected by the caller (type-specific actions
 *     like Open profile / Open editor / Convert annotation that
 *     don't belong in the cross-cutting registry).
 *   - Section dividers driven by `groupBySection` so the registry
 *     order is the rendered order without per-call configuration.
 */

import {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import { PopoverPortal } from '@/components/ui/popover-portal';
import { CommandIcon } from './command-icons';
import {
  filterMenuCommands,
  formatShortcut,
  groupBySection,
  type CommandContext,
  type CommandIconId,
  type WorkspaceCommand,
} from '@/lib/workspace/commands';

// ----------------------------------------------------------------------
// Item types
// ----------------------------------------------------------------------

/**
 * One menu row. Either a regular action, a submenu trigger, a
 * pre-built section header, or a literal divider. The flat union
 * keeps the renderer simple — we don't need a separate components
 * tree per item kind.
 */
export type CanvasMenuItem =
  | {
      kind: 'action';
      id: string;
      label: string;
      icon?: CommandIconId;
      shortcut?: string;
      tone?: 'default' | 'danger';
      disabled?: boolean;
      onSelect: () => void;
    }
  | {
      kind: 'submenu';
      id: string;
      label: string;
      icon?: CommandIconId;
      disabled?: boolean;
      items: CanvasMenuItem[];
    }
  | {
      kind: 'separator';
      id?: string;
    };

// ----------------------------------------------------------------------
// Layout
// ----------------------------------------------------------------------

const MENU_MIN_WIDTH = 220;
const MENU_MAX_WIDTH = 300;
const VIEWPORT_MARGIN = 8;
const SUBMENU_OFFSET = 2;

// ----------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------

export interface CanvasContextMenuProps {
  /** Top-left anchor in viewport coords; `null` closes the menu. */
  anchor: { x: number; y: number } | null;
  items: CanvasMenuItem[];
  onClose: () => void;
  ariaLabel?: string;
}

export function CanvasContextMenu({
  anchor,
  items,
  onClose,
  ariaLabel = 'Canvas context menu',
}: CanvasContextMenuProps) {
  if (!anchor) return null;
  return (
    <MenuPanel
      anchor={anchor}
      items={items}
      onClose={onClose}
      ariaLabel={ariaLabel}
      depth={0}
    />
  );
}

// ----------------------------------------------------------------------
// Helpers for building items from the registry
// ----------------------------------------------------------------------

/**
 * Translate a list of registry commands into menu rows, inserting
 * a `separator` between consecutive sections. Items disabled by the
 * predicate are filtered out entirely (we don't render them greyed
 * out — Figma / Confluence both prefer hiding to disabling for
 * canvas commands).
 *
 * `extras` lets the caller splice in type-specific rows that don't
 * fit the cross-cutting registry (e.g. "Open profile" for an agent
 * widget). Each extras section gets its own divider afterwards.
 */
export function buildMenuItemsFromCommands(
  commands: WorkspaceCommand[],
  ctx: CommandContext,
  scope: 'selection' | 'pane',
  opts?: {
    /** Items to splice in BEFORE the registry-driven rows. */
    leading?: CanvasMenuItem[];
    /** Items to splice in BETWEEN sections, keyed by section name. */
    bySection?: Partial<Record<string, CanvasMenuItem[]>>;
    /** Items to splice in AFTER all registry-driven rows. */
    trailing?: CanvasMenuItem[];
  },
): CanvasMenuItem[] {
  const filtered = filterMenuCommands(commands, ctx, scope);
  const sections = groupBySection(filtered);
  const rows: CanvasMenuItem[] = [];
  if (opts?.leading?.length) {
    rows.push(...opts.leading);
    rows.push({ kind: 'separator' });
  }
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    if (section.section === 'arrange') {
      // Collapse the 6 align + 2 distribute commands into two
      // submenus so the menu stays compact.
      const aligns = section.commands.filter((c) => c.id.startsWith('align-'));
      const distributes = section.commands.filter((c) =>
        c.id.startsWith('distribute-'),
      );
      if (aligns.length > 0) {
        rows.push({
          kind: 'submenu',
          id: 'arrange-align',
          label: 'Align',
          icon: 'align-center-h',
          items: aligns.map<CanvasMenuItem>((c) => ({
            kind: 'action',
            id: c.id,
            label: c.label,
            icon: c.icon,
            shortcut: formatShortcut(c.shortcut, ctx.platform),
            tone: c.tone,
            onSelect: () => void c.run(ctx),
          })),
        });
      }
      if (distributes.length > 0) {
        rows.push({
          kind: 'submenu',
          id: 'arrange-distribute',
          label: 'Distribute',
          icon: 'distribute-h',
          items: distributes.map<CanvasMenuItem>((c) => ({
            kind: 'action',
            id: c.id,
            label: c.label,
            icon: c.icon,
            shortcut: formatShortcut(c.shortcut, ctx.platform),
            tone: c.tone,
            onSelect: () => void c.run(ctx),
          })),
        });
      }
    } else {
      for (const c of section.commands) {
        rows.push({
          kind: 'action',
          id: c.id,
          label: c.label,
          icon: c.icon,
          shortcut: formatShortcut(c.shortcut, ctx.platform),
          tone: c.tone,
          onSelect: () => void c.run(ctx),
        });
      }
    }
    const sectionExtras = opts?.bySection?.[section.section];
    if (sectionExtras?.length) {
      rows.push(...sectionExtras);
    }
    if (i < sections.length - 1) {
      rows.push({ kind: 'separator' });
    }
  }
  if (opts?.trailing?.length) {
    if (rows.length > 0 && rows[rows.length - 1].kind !== 'separator') {
      rows.push({ kind: 'separator' });
    }
    rows.push(...opts.trailing);
  }
  return collapseSeparators(rows);
}

/** Remove leading / trailing / consecutive separators. */
function collapseSeparators(rows: CanvasMenuItem[]): CanvasMenuItem[] {
  const out: CanvasMenuItem[] = [];
  for (const row of rows) {
    if (row.kind === 'separator') {
      if (out.length === 0) continue;
      if (out[out.length - 1].kind === 'separator') continue;
    }
    out.push(row);
  }
  while (out.length > 0 && out[out.length - 1].kind === 'separator') out.pop();
  return out;
}

// ----------------------------------------------------------------------
// Internal: the actual rendered panel (recursive for submenus)
// ----------------------------------------------------------------------

interface MenuPanelProps {
  anchor: { x: number; y: number };
  items: CanvasMenuItem[];
  onClose: () => void;
  ariaLabel: string;
  depth: number;
  /** When `true`, the panel renders as a submenu — flips horizontally
   *  rather than upward when it would overflow the viewport, so it
   *  always opens away from the parent column. */
  isSubmenu?: boolean;
}

function MenuPanel({
  anchor,
  items,
  onClose,
  ariaLabel,
  depth,
  isSubmenu = false,
}: MenuPanelProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number>(-1);
  const [submenuAnchor, setSubmenuAnchor] = useState<
    { x: number; y: number } | null
  >(null);

  // Reset position whenever the anchor changes — keeps the panel
  // glued to the new pointer / submenu trigger origin.
  useEffect(() => {
    setPos(anchor);
    setActiveIndex(-1);
    setOpenSubmenuIndex(-1);
    setSubmenuAnchor(null);
  }, [anchor]);

  // Measure + flip. Same pattern as `file-context-menu.tsx`: callback
  // ref fires synchronously after mount so we can correct overflow
  // in the SAME frame as the initial render.
  const measureRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const w = rect.width || MENU_MIN_WIDTH;
      const h = rect.height || 0;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let nx = anchor.x;
      let ny = anchor.y;
      if (isSubmenu) {
        // Submenus prefer to open to the right; flip to the left
        // when they'd overflow. Y handles the same way as root.
        if (nx + w + VIEWPORT_MARGIN > vw) {
          nx = Math.max(VIEWPORT_MARGIN, anchor.x - w - SUBMENU_OFFSET * 2);
        }
        if (ny + h + VIEWPORT_MARGIN > vh) {
          ny = Math.max(VIEWPORT_MARGIN, vh - h - VIEWPORT_MARGIN);
        }
      } else {
        if (nx + w + VIEWPORT_MARGIN > vw) {
          nx = Math.max(VIEWPORT_MARGIN, vw - w - VIEWPORT_MARGIN);
        }
        if (ny + h + VIEWPORT_MARGIN > vh) {
          ny = Math.max(VIEWPORT_MARGIN, anchor.y - h);
        }
      }
      if (nx !== anchor.x || ny !== anchor.y) {
        setPos((prev) =>
          prev && prev.x === nx && prev.y === ny ? prev : { x: nx, y: ny },
        );
      }
    },
    [anchor, isSubmenu],
  );

  // Outside-click + Escape dismiss for the root panel. Submenus
  // close via their parent (clicking a row dispatches `onClose`
  // upward), so we skip these listeners when isSubmenu is true to
  // avoid double-dispatching the dismiss.
  useEffect(() => {
    if (isSubmenu) return;
    const onPointer = (e: PointerEvent) => {
      const panel = panelRef.current;
      if (!panel) return;
      if (e.target instanceof Node && panel.contains(e.target)) return;
      // Any open submenu also captures clicks via the same listener
      // (it shares the document). We close only when the click misses
      // BOTH the root and any open submenu — the submenu's own
      // listener handles that case.
      onClose();
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, [isSubmenu, onClose]);

  // Keyboard navigation. We focus the first interactive row when
  // the panel mounts so arrow-keys "just work" without the user
  // having to mouse into the menu first.
  useEffect(() => {
    if (!pos) return;
    // Defer one frame so the buttons exist in the DOM.
    const id = window.requestAnimationFrame(() => {
      const first = firstFocusableIndex(items);
      if (first >= 0) {
        itemRefs.current[first]?.focus({ preventScroll: true });
        setActiveIndex(first);
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [items, pos]);

  const focusIndex = useCallback(
    (idx: number) => {
      const node = itemRefs.current[idx];
      if (!node) return;
      node.focus({ preventScroll: true });
      setActiveIndex(idx);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = nextFocusableIndex(items, activeIndex);
        if (next >= 0) focusIndex(next);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = prevFocusableIndex(items, activeIndex);
        if (prev >= 0) focusIndex(prev);
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        const first = firstFocusableIndex(items);
        if (first >= 0) focusIndex(first);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        const last = lastFocusableIndex(items);
        if (last >= 0) focusIndex(last);
        return;
      }
      if (e.key === 'ArrowLeft' && isSubmenu) {
        e.preventDefault();
        onClose();
        return;
      }
    },
    [activeIndex, focusIndex, isSubmenu, items, onClose],
  );

  if (!pos) return null;

  const style: CSSProperties = {
    position: 'fixed',
    left: pos.x,
    top: pos.y,
    zIndex: 200 + depth,
    minWidth: MENU_MIN_WIDTH,
    maxWidth: MENU_MAX_WIDTH,
  };

  return (
    <>
      <PopoverPortal style={style} role="menu" ariaLabel={ariaLabel}>
        <div
          ref={measureRef}
          onKeyDown={handleKeyDown}
          className={cn(
            'glass-panel rounded-xl py-1 text-sm shadow-2xl',
            'origin-top-left animate-[wsCtxMenuIn_120ms_cubic-bezier(0.2,0.9,0.2,1)_forwards]',
            'outline-none',
          )}
          tabIndex={-1}
        >
          {items.map((row, idx) => {
            if (row.kind === 'separator') {
              return (
                <div
                  key={row.id ?? `sep-${idx}`}
                  role="separator"
                  className="my-1 h-px bg-border/60"
                />
              );
            }
            if (row.kind === 'submenu') {
              const isOpen = openSubmenuIndex === idx;
              return (
                <SubmenuRow
                  key={row.id}
                  row={row}
                  active={activeIndex === idx}
                  isOpen={isOpen}
                  buttonRef={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  onFocus={() => setActiveIndex(idx)}
                  onHover={(e) => {
                    if (row.disabled) return;
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    setSubmenuAnchor({
                      x: rect.right + SUBMENU_OFFSET,
                      y: rect.top,
                    });
                    setOpenSubmenuIndex(idx);
                  }}
                  onActivate={(e) => {
                    if (row.disabled) return;
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    setSubmenuAnchor({
                      x: rect.right + SUBMENU_OFFSET,
                      y: rect.top,
                    });
                    setOpenSubmenuIndex(idx);
                  }}
                />
              );
            }
            return (
              <ActionRow
                key={row.id}
                row={row}
                active={activeIndex === idx}
                buttonRef={(el) => {
                  itemRefs.current[idx] = el;
                }}
                onFocus={() => {
                  setActiveIndex(idx);
                  setOpenSubmenuIndex(-1);
                }}
                onActivate={() => {
                  if (row.disabled) return;
                  haptic('light');
                  row.onSelect();
                  onClose();
                }}
              />
            );
          })}
        </div>
      </PopoverPortal>
      {openSubmenuIndex >= 0 &&
        items[openSubmenuIndex]?.kind === 'submenu' &&
        submenuAnchor && (
          <MenuPanel
            anchor={submenuAnchor}
            items={(items[openSubmenuIndex] as { items: CanvasMenuItem[] }).items}
            onClose={onClose}
            ariaLabel={`${
              (items[openSubmenuIndex] as { label: string }).label
            } submenu`}
            depth={depth + 1}
            isSubmenu
          />
        )}
    </>
  );
}

// ----------------------------------------------------------------------
// Row renderers
// ----------------------------------------------------------------------

function ActionRow({
  row,
  active,
  buttonRef,
  onFocus,
  onActivate,
}: {
  row: Extract<CanvasMenuItem, { kind: 'action' }>;
  active: boolean;
  buttonRef: (el: HTMLButtonElement | null) => void;
  onFocus: () => void;
  onActivate: () => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="menuitem"
      disabled={row.disabled}
      tabIndex={active ? 0 : -1}
      onFocus={onFocus}
      onMouseEnter={(e) => (e.currentTarget as HTMLElement).focus()}
      onClick={onActivate}
      className={cn(
        'group flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors outline-none',
        row.disabled
          ? 'cursor-not-allowed text-muted-foreground/70'
          : 'hover:bg-foreground/8 focus:bg-foreground/8 active:bg-foreground/12',
        row.tone === 'danger' && !row.disabled && 'text-red-600 dark:text-red-400',
      )}
    >
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center opacity-80">
        {row.icon ? <CommandIcon id={row.icon} className="h-4 w-4" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{row.label}</span>
      {row.shortcut && (
        <span className="ml-2 shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {row.shortcut}
        </span>
      )}
    </button>
  );
}

function SubmenuRow({
  row,
  active,
  isOpen,
  buttonRef,
  onFocus,
  onHover,
  onActivate,
}: {
  row: Extract<CanvasMenuItem, { kind: 'submenu' }>;
  active: boolean;
  isOpen: boolean;
  buttonRef: (el: HTMLButtonElement | null) => void;
  onFocus: () => void;
  onHover: (e: ReactPointerEvent<HTMLButtonElement>) => void;
  onActivate: (e: ReactPointerEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      role="menuitem"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      disabled={row.disabled}
      tabIndex={active ? 0 : -1}
      onFocus={onFocus}
      onMouseEnter={(e) => {
        e.currentTarget.focus();
        onHover(e as unknown as ReactPointerEvent<HTMLButtonElement>);
      }}
      onClick={(e) => onActivate(e as unknown as ReactPointerEvent<HTMLButtonElement>)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(e as unknown as ReactPointerEvent<HTMLButtonElement>);
        }
      }}
      className={cn(
        'group flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors outline-none',
        row.disabled
          ? 'cursor-not-allowed text-muted-foreground/70'
          : 'hover:bg-foreground/8 focus:bg-foreground/8',
      )}
    >
      <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center opacity-80">
        {row.icon ? <CommandIcon id={row.icon} className="h-4 w-4" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate">{row.label}</span>
      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
    </button>
  );
}

// ----------------------------------------------------------------------
// Focus helpers
// ----------------------------------------------------------------------

function isFocusable(row: CanvasMenuItem): boolean {
  if (row.kind === 'separator') return false;
  if (row.disabled) return false;
  return true;
}

function firstFocusableIndex(items: CanvasMenuItem[]): number {
  for (let i = 0; i < items.length; i++) if (isFocusable(items[i])) return i;
  return -1;
}

function lastFocusableIndex(items: CanvasMenuItem[]): number {
  for (let i = items.length - 1; i >= 0; i--) if (isFocusable(items[i])) return i;
  return -1;
}

function nextFocusableIndex(items: CanvasMenuItem[], from: number): number {
  for (let i = from + 1; i < items.length; i++) if (isFocusable(items[i])) return i;
  // wrap to first
  return firstFocusableIndex(items);
}

function prevFocusableIndex(items: CanvasMenuItem[], from: number): number {
  for (let i = from - 1; i >= 0; i--) if (isFocusable(items[i])) return i;
  return lastFocusableIndex(items);
}
